// create page

import React from 'react';
import {
  AppBar,
  Box,
  Container,
  Divider,
  Grid,
  IconButton,
  Stack,
  Skeleton,
  Toolbar,
  Typography,
} from '@mui/material';
import { OutlinedIcons } from '@marageti/z4-lib';
import { ReactComponent as ZicassoSVG } from '../../assets/svg/zicasso.svg';
import { TripHeroImage } from '../../components/trips';

const { OutlinedUserIcon } = OutlinedIcons;

const APP_BAR_HEIGHT = '92px';

const SkeletonInfoBox = (): React.ReactElement => (
  <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2 }}>
    <Typography variant="h6">
      <Skeleton width="50%" />
    </Typography>
    <Skeleton variant="text" sx={{ fontSize: '0.875rem', width: '70%' }} />
    <Skeleton variant="text" sx={{ fontSize: '0.875rem', width: '90%' }} />
    <Skeleton variant="rectangular" width={150} height={36} sx={{ marginTop: 2, borderRadius: 1 }} />
  </Box>
);

const SkeletonTripDetailsBackground = ({ photoId, destinations }: { photoId: string | null, destinations: string | null }): React.ReactElement => (
  <>
    <AppBar
      position="fixed"
      sx={{
        top: 0,
        width: '100%',
        zIndex: 1100,
        backgroundColor: 'transparent',
        height: APP_BAR_HEIGHT,
      }}
      elevation={0}
    >
      <Toolbar sx={{ height: '100%', justifyContent: 'space-between' }}>
        <Box display="flex" alignItems="center">
          <ZicassoSVG />
        </Box>
        <Stack direction="row" spacing={4} alignItems="center">
          <Typography
            variant="button"
            component="div"
            sx={{
              fontSize: '0.75rem', color: 'black', fontWeight: 400, letterSpacing: '1.6px',
            }}
          >
            MY TRIPS
          </Typography>
          <IconButton color="primary">
            <OutlinedUserIcon />
          </IconButton>
        </Stack>
      </Toolbar>
    </AppBar>
    <Divider sx={{
      position: 'fixed', top: APP_BAR_HEIGHT, width: '100%', zIndex: 1100,
    }}
    />
    <Container
      maxWidth="xl"
      sx={{
        mt: APP_BAR_HEIGHT,
        minHeight: `calc(100vh - ${APP_BAR_HEIGHT})`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ py: 2, flexGrow: 1 }}>
        <Grid
          container
          spacing={{
            desktop: 6,
            mobile: 4,
          }}
        >
          <Grid item xs={12} sm={12} md={6}>
            {photoId ? (
              <Box
                sx={{
                  width: '100%',
                  height: '80vh',
                  marginTop: 1,
                  borderRadius: '16px',
                  overflow: 'hidden',
                }}
              >
                <TripHeroImage
                  photoId={photoId}
                />
              </Box>
            ) : (
              <Skeleton
                variant="rectangular"
                sx={{
                  width: '100%',
                  height: '80vh',
                  marginTop: 1,
                  borderRadius: '16px',
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Stack
              spacing={4}
              sx={{
                flex: { desktop: '1 1 auto' },
                maxWidth: {
                  desktop: '642px',
                },
                mb: 6,
              }}
            >
              {/* Mock Trip Title Area */}
              <Box>
                <Typography sx={{ marginBottom: 0.75 }} variant="subtitle1" color="text.secondary">Trip To</Typography>
                <Typography
                  data-e2e="ztest-trip-to"
                  variant="h2"
                  sx={{
                    marginBottom: 2,
                  }}
                >
                  {destinations || <Skeleton width={200} />}
                </Typography>
                <Skeleton variant="rectangular" width={60} height={22} sx={{ marginTop: 1, borderRadius: '16px' }} />
              </Box>

              {/* Mock Booking Details Area */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  <Skeleton width="40%" />
                </Typography>
                <Stack spacing={1.5}>
                  <Typography variant="body1">
                    <strong>
                      <Skeleton width="25%" />
                    </strong>
                    {' '}
                    <Skeleton width="50%" />
                  </Typography>
                  <Typography variant="body1">
                    <strong>
                      <Skeleton width="45%" />
                    </strong>
                    {' '}
                    <Skeleton width="20%" />
                  </Typography>
                </Stack>
              </Box>
              <SkeletonInfoBox />
              <SkeletonInfoBox />
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Container>
  </>
);

export default SkeletonTripDetailsBackground;
